import { useEffect, useState } from 'react';
import { cn } from '../lib/utils';
import type { ResolvedBranding } from './types';

interface PortalBrandProps {
  branding: ResolvedBranding;
  compact?: boolean;
}

/**
 * Uses an app's public/logo.png when it loads successfully, otherwise keeps
 * the configured icon text and portal label visible.
 */
export function PortalBrand({ branding, compact = false }: PortalBrandProps) {
  const logoSrc = branding.logoSrc ?? '/logo.png';
  const [logoLoaded, setLogoLoaded] = useState(false);

  useEffect(() => {
    setLogoLoaded(false);
  }, [logoSrc]);

  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-2.5',
        (compact || logoLoaded) && 'justify-center',
        logoLoaded && 'w-full',
      )}
    >
      <img
        src={logoSrc}
        alt={branding.name}
        className={cn(
          'block h-auto max-h-20 w-auto max-w-[176px] object-contain object-center',
          compact && 'max-h-[30px] max-w-[30px] object-center',
          !logoLoaded && 'hidden',
        )}
        draggable={false}
        onLoad={() => setLogoLoaded(true)}
        onError={() => setLogoLoaded(false)}
      />
      {!logoLoaded && (
        <>
          <div
            className="flex h-[30px] min-w-[30px] flex-shrink-0 items-center justify-center rounded-[8px] bg-primary px-1 text-[12px] font-extrabold tracking-[-1px] text-primary-foreground"
            aria-hidden
          >
            {branding.iconText}
          </div>
          {compact ? (
            <span className="sr-only">{branding.name}</span>
          ) : (
            <div className="min-w-0 truncate text-[15px] font-bold leading-tight">
              {branding.shortLabel}
              {branding.shortSuffix && (
                <span className="ml-1 font-normal text-muted-foreground">
                  {branding.shortSuffix}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
